/* Native sign-in intent, separate privacy permission and issued-link cancellation. */
(() => {
  'use strict';
  if (window.ThryftScoutAccount) return;
  const config = JSON.parse(document.querySelector('[data-scout-account-config]')?.textContent || '{}');
  const root = (config.root || '/').replace(/\/?$/, '/');
  const t = key => config.copy?.[key] || key;
  const endpoint = root + 'apps/thryft-recommendations/scout';
  const key = 'thryft-scout-intent-v2';
  const ttl = 15 * 60 * 1000;
  const money = (value, currency = 'SGD') => currency === 'SGD' ? `S$${Number(value).toFixed(2)}` : `${/^[A-Z]{3}$/.test(currency) ? currency + ' ' : ''}${Number(value).toFixed(2)}`;
  function localProduct(value) {
    try {
      if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u001f]/.test(value)) return null;
      const url = new URL(value, location.origin);
      const prefix = root.replace(/\/$/, '') + '/products/';
      if (url.origin !== location.origin || !url.pathname.startsWith(prefix) || !/^[a-zA-Z0-9_-]+$/.test(url.pathname.slice(prefix.length))) return null;
      const preview = url.searchParams.get('preview_theme_id');
      if (preview !== null && !/^\d+$/.test(preview)) return null;
      return url.pathname + (preview ? '?preview_theme_id=' + preview : '');
    } catch { return null; }
  }
  function managementPath(value) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:account(?:\/|$)|pages\/)/.test(url.pathname) || /[\\\u0000-\u001f]/.test(value)) return null;
      const targetPreview = url.searchParams.get('preview_theme_id');
      if (targetPreview !== null && !/^\d+$/.test(targetPreview)) url.searchParams.delete('preview_theme_id');
      const preview = new URL(location.href || location.origin + location.pathname).searchParams.get('preview_theme_id');
      if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      return url.pathname + url.search + url.hash;
    } catch { return null; }
  }
  function managementSignInPath() {
    const login = managementPath(config.login || root + 'account/login');
    if (!login) return null;
    const url = new URL(login, location.origin);
    const target = managementPath(location.href || location.origin + location.pathname);
    if (target) url.searchParams.set('return_url', target);
    return url.pathname + url.search;
  }
  function returnTarget(intent) { const url = new URL(intent.url, location.origin); url.searchParams.set('variant', intent.variant_id); url.searchParams.set('scout','open'); return url.pathname + url.search; }
  function privacyState() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      const methods = ['analyticsProcessingAllowed','preferencesProcessingAllowed','marketingAllowed'];
      if (!methods.every(name => typeof privacy?.[name] === 'function')) return null;
      const flags = methods.map(name => privacy[name]());
      return flags.every(value => typeof value === 'boolean') ? flags.every(Boolean) : null;
    } catch { return null; }
  }
  function privacyAllowed() { return privacyState() === true; }
  function preferencesState() {
    try {
      const allowed = window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.();
      return typeof allowed === 'boolean' ? allowed : null;
    } catch { return null; }
  }
  function readIntent() {
    try {
      const intent = JSON.parse(sessionStorage.getItem(key));
      if (!intent || !localProduct(intent.url) || !/^\d+$/.test(intent.product_id) || !/^\d+$/.test(intent.variant_id) || !Number.isFinite(intent.at) || Date.now() - intent.at > ttl || intent.at > Date.now() || !intent.criteria || typeof intent.criteria !== 'object') throw Error('intent');
      if (intent.remember_interest !== undefined && typeof intent.remember_interest !== 'boolean') throw Error('intent');
      const allowed = ['scope', 'condition', 'max_price', 'media_condition', 'sleeve_condition'];
      if (Object.keys(intent.criteria).some(k => !allowed.includes(k)) || Object.values(intent.criteria).some(v => typeof v !== 'string' || v.length > 80)) throw Error('intent');
      return intent;
    } catch { try { sessionStorage.removeItem(key); } catch {} return null; }
  }
  async function api(path, body, signal) {
    const request = new AbortController();
    const abort = () => request.abort();
    if (signal?.aborted) request.abort();
    else signal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 10000);
    try {
      const response = await fetch(endpoint + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: request.signal, headers: body ? { 'Content-Type': 'application/json' } : {}, body: body ? JSON.stringify(body) : undefined });
      let result;
      try { result = await response.json(); } catch { throw Error(t('account_unavailable')); }
      if (!response.ok) throw Object.assign(Error(result.error || t('account_unavailable')), { status: response.status });
      return result;
    } catch (error) {
      if (error.name === 'AbortError' && !signal?.aborted) throw Error(t('account_unavailable'));
      throw error;
    } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
  }
  function signIn(product, criteria, rememberInterest) {
    // Shopify's preview bar can remove the query while the draft remains active.
    const theme = window.Shopify?.theme;
    const queryPreview = new URL(location.href || location.origin + location.pathname).searchParams.get('preview_theme_id');
    const preview = queryPreview === null && theme?.role === 'unpublished' && /^\d+$/.test(String(theme.id)) ? String(theme.id) : queryPreview;
    const url = localProduct(product.product_url + (preview ? '?preview_theme_id=' + encodeURIComponent(preview) : ''));
    if (!url || !/^\d+$/.test(product.product_id) || !/^\d+$/.test(product.variant_id)) throw Error(t('account_open_product'));
    const safe = Object.fromEntries(['scope','condition','max_price','media_condition','sleeve_condition'].map(k => [k, String(criteria[k] || '')]));
    try { sessionStorage.setItem(key, JSON.stringify({ url, product_id: product.product_id, variant_id: product.variant_id, at: Date.now(), criteria: safe, ...(typeof rememberInterest === 'boolean' ? { remember_interest: rememberInterest } : {}) })); } catch { throw Error(t('account_storage')); }
    location.assign((config.login || root + 'account/login') + '?return_url=' + encodeURIComponent(returnTarget({url,variant_id:product.variant_id})));
  }
  function nativeReturn() {
    const intent = readIntent();
    if (!intent) return;
    const target = returnTarget(intent);
    document.querySelectorAll('form[action*="/account/login"],form[action*="/account"]').forEach(form => {
      if (!form.querySelector('[name="customer[password]"]') && !form.querySelector('[name="customer[email]"]')) return;
      let input = form.querySelector('[name="return_to"]');
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'return_to'; form.append(input); }
      input.value = target;
    });
    document.querySelectorAll('a[href*="/account/register"],a[href*="/account/login"]').forEach(link => {
      const url = new URL(link.href, location.origin);
      if (url.origin !== location.origin) return;
      url.searchParams.set('return_url', target); link.href = url.pathname + url.search;
    });
    if (config.authenticated && /\/account\/?$/.test(location.pathname)) location.replace(target);
  }
  window.ThryftScoutAccount = {
    money, localProduct, managementPath, managementSignInPath, readIntent, signIn, api, privacyAllowed, privacyState, preferencesState,
    session: signal => api('/account', null, signal),
    takeIntent(url, id) {
      const intent = readIntent();
      if (!intent || new URL(intent.url, location.origin).pathname !== new URL(localProduct(url) || '/', location.origin).pathname || intent.product_id !== id) return null;
      try { sessionStorage.removeItem(key); } catch {}
      return intent;
    }
  };
  // Only already-issued account URLs use this cancellation compatibility surface.
  async function cancelCompatibility() {
    const host = document.querySelector('[data-scout-cancel-compat]');
    if (!host) return;
    const status = host.querySelector('[role="status"]');
    try {
      let session = await api('/account');
      let refreshedAt = Date.now();
      if (!session.authenticated) {
        const login = document.createElement('a'); login.textContent = t('sign_in');
        login.href = managementSignInPath() || root + 'account/login'; host.append(login); return;
      }
      for (const watch of session.watches || []) {
        if (!['active', 'paused'].includes(watch.status)) continue;
        const row = document.createElement('p');
        const title = document.createElement('span'); title.textContent = (watch.criteria?.source?.title || t('account_saved_title')) + ' ';
        const stop = document.createElement('button'); stop.type = 'button'; stop.className = 'btn btn--text-link'; stop.textContent = t('stop_request');
        async function reviewSession() {
          const current = await api('/account');
          if (host.isConnected === false) return;
          const owned = (current.watches || []).find(item => item.id === watch.id);
          if (!current.authenticated || !owned) throw Error(t('account_unavailable'));
          session = current; refreshedAt = Date.now();
          Object.assign(watch, owned);
          if (watch.status === 'stopped') { row.remove(); status.textContent = t('request_stopped'); }
          else status.textContent = t('session_review_cancel');
        }
        stop.addEventListener('click', async () => {
          if (stop.disabled || host.isConnected === false) return;
          stop.disabled = true; status.textContent = t('stopping');
          try {
            if (Date.now() - refreshedAt >= 14 * 60 * 1000) { await reviewSession(); return; }
            const result = await api('/account/watch', {contract:'once-shelf-v1',watch_id:watch.id,revision:watch.revision,nonce:session.nonce,action:'stop'});
            if (result.watch?.status !== 'stopped') throw Error(t('unavailable'));
            row.remove(); status.textContent = t('request_stopped');
          } catch (error) {
            status.textContent = error.message;
            if (error.status === 403 && error.message === 'Your session expired. Refresh Scout and try again.') {
              try { await reviewSession(); } catch (refreshError) { status.textContent = refreshError.message; }
            }
          } finally { stop.disabled = false; }
        });
        row.append(title, stop); host.append(row);
      }
      status.textContent = '';
    } catch (error) { status.textContent = error.message; }
  }
  let withdrawing = false;
  async function syncPrivacy() {
    document.dispatchEvent?.(new CustomEvent('scout:privacy-ready'));
    if (withdrawing || privacyState() !== false && preferencesState() !== false) return;
    withdrawing = true;
    try {
      const current = await api('/account');
      if (!current.authenticated) return;
      if (preferencesState() === false) {
        let remaining;
        do { remaining = (await api('/account/watch', {nonce:current.nonce,action:'withdraw_interest',preferences_allowed:false})).remaining === true; }
        while (remaining && preferencesState() === false);
      }
      // Compatibility for prior, separately authorised Discovery reuse only.
      if (privacyState() === false) for (const watch of current.watches || []) {
        if (watch.discovery_use_wants) await api('/account/watch', {watch_id:watch.id,revision:watch.revision,nonce:current.nonce,action:'discovery',use_wants:false,discovery_privacy_allowed:false});
      }
    } catch {
      document.dispatchEvent?.(new CustomEvent('scout:privacy-error', {detail:t('privacy_update_failed')}));
    } finally { withdrawing = false; }
  }
  document.addEventListener('visitorConsentCollected', syncPrivacy);
  nativeReturn(); cancelCompatibility();
  if (privacyState() !== null || preferencesState() !== null) syncPrivacy();
  else window.Shopify?.loadFeatures?.([{name:'consent-tracking-api',version:'0.1'}], error => { if (!error) syncPrivacy(); });
})();
